/**
 * **El indicador de espera de la aplicación, con dueño: cada petición tiene su TESTIGO, y el
 * indicador se apaga cuando no queda ninguno.**
 *
 * `app.preloader` de Framework7 no es de la pantalla que lo enciende: es un modal de la
 * APLICACIÓN, hay uno solo, y sus dos operaciones no llevan cuenta de nadie —`show()` no hace nada
 * si ya está puesto y `hide()` lo quita siempre, venga de quien venga—. O sea que dos esperas que
 * se solapan **no son dos preloaders**: son uno, y el primero que acabe se lo quita al otro.
 *
 * Eso es lo que pasaba al anular unas vacaciones (revisión CODEX del PR #1735, ronda 4 del
 * 07/10/2026, `mis-vacaciones.vue:921`): la acción enciende su espera, el servidor contesta,
 * `exito` arranca la recarga del resumen —que enciende la suya— y al volver, el `then` final de la
 * acción llamaba a `hide()`. Quedaban en pantalla el saldo y la solicitud de ANTES, sin nada que
 * dijera que se estaban actualizando. Y el mismo orden, con el mismo resultado, en el envío de una
 * solicitud nueva (`enviada` → `trasEnviar` → `cargar`) y al navegar a una ruta con barrera
 * mientras una carga sigue en vuelo.
 *
 * El contrato, que es uno y vive aquí (decisión técnica de Donchurru del 17/10/2026 en la #59):
 *
 * > **Cada petición pide su testigo y suelta el suyo.** El indicador se enciende cuando aparece el
 * > primero y se apaga cuando no queda ninguno. Soltar un testigo que ya no está —porque lo
 * > relevó otra petición, porque se salió de la pantalla o porque cambió la sesión— **no apaga
 * > nada**: una respuesta vieja no puede quitarle el indicador a la espera nueva.
 *
 * ## Por qué un testigo y no una cuenta
 *
 * Porque la cuenta sola no sabe **de quién** es cada espera. Con `mostrar()`/`ocultar()` a secas,
 * el `catch` de una petición ya relevada bajaba el contador de la que estaba en vuelo, y bastaba
 * una llamada de más —o una de menos— para dejar el indicador puesto para siempre o apagado sobre
 * una pantalla que sigue cargando. Cada llamante tenía entonces que llevar su propia contabilidad
 * de «¿sigo siendo el dueño?», y eso es exactamente el reparto por llamante que trae una ronda de
 * revisión por cada sitio nuevo: la pantalla de «Mis vacaciones» llevaba su registro de cargas con
 * `show()`/`hide()` directos, la barrera de ruta su contador y los ayudantes de pantalla el suyo,
 * tres contabilidades para un único modal.
 *
 * Con el testigo la pregunta se contesta aquí una vez: soltar es idempotente y solo cuenta si ese
 * testigo sigue vivo.
 *
 * ## Las dos cosas que NO hace
 *
 * - **No cancela nada.** Soltar la espera es decir «yo ya no espero», no «la petición se para». De
 *   descartar la respuesta que llega tarde se encargan el carril (`carrilPeticiones`),
 *   `respuestaVigente` y `accionDeLaSesion`.
 * - **No sabe de sesiones.** Que una espera se suelte cuando la sesión se va —y no cuando el
 *   servidor conteste, que puede no pasar nunca— es de `preloaderDeLaSesion`; que se suelte al
 *   abandonar la navegación es de `barreraRuta`. Aquí solo se sabe cuántos testigos quedan.
 *
 * Y `show()` se llama en **cada** petición, no solo en la primera: en Framework7 es idempotente
 * —si el modal ya está puesto no hace nada— y así una espera nueva vuelve a encender el indicador
 * aunque un `hide()` de fuera de esta cuenta lo haya quitado por su cuenta (el calendario tiene
 * treinta de esos, de antes de todo esto).
 */

/**
 * Los testigos vivos. Un `Set` y no un contador para poder saber **si este** sigue siendo uno de
 * ellos; el orden de inserción no importa, pero es el que tiene.
 */
const vivos = new Set();

/**
 * Pide el indicador y devuelve **el testigo de esta espera**, que es lo único con lo que se puede
 * soltar. Quien lo pide lo guarda; no hay forma de soltar la espera de otro.
 */
export function pedirEspera(app) {
    // La instancia de Framework7 viaja con el testigo: quien lo suelta puede estar ya destruido, y
    // al apagar el modal hace falta una con la que hacerlo.
    const testigo = { app: app || null };
    vivos.add(testigo);
    if (app && app.preloader) app.preloader.show();
    return testigo;
}

/**
 * Suelta **esa** espera. Devuelve si el testigo seguía vivo, que es lo que distingue «he soltado lo
 * mío» de «lo mío ya no estaba»: una respuesta de una petición relevada, de otra sesión o de una
 * pantalla de la que ya se salió llega aquí igual y no tiene que apagar nada.
 *
 * El indicador se apaga **solo al soltar el último**, que es la razón de ser del módulo.
 */
export function soltarEspera(testigo) {
    if (!testigo || !vivos.delete(testigo)) return false;
    if (vivos.size === 0) {
        const app = testigo.app;
        if (app && app.preloader) app.preloader.hide();
    }
    return true;
}

/** Si esta espera sigue vigente. Para quien tenga que decidir con eso, y para las pruebas. */
export function esperaVigente(testigo) {
    return !!testigo && vivos.has(testigo);
}

/** Cuántas esperas hay en vuelo. Para quien tenga que decidir con eso, y para las pruebas. */
export function esperasEnVuelo() {
    return vivos.size;
}

/**
 * #472 · **El `cargando` de un carril (`carrilPeticiones`) sobre el indicador de la aplicación.**
 *
 * Las pantallas que tapan la pantalla entera mientras cargan encendían el preloader antes de lanzar
 * y lo apagaban en el `then` de la promesa del carril, que se cumple también cuando la respuesta se
 * descarta: con dos cargas seguidas, la vieja lo apagaba con la vigente todavía en vuelo. Con esto
 * lo enciende y lo apaga el carril, que es quien sabe cuándo ya no queda nadie que pueda escribir:
 *
 *   this.carril = carrilPeticiones({ cargando: esperaDelCarril(this) });
 *
 * Cada `true` pide un testigo nuevo y suelta el anterior **después**, para que el indicador no
 * parpadee entre una carga y la que la releva; el `false` suelta el último. Va por testigo, como
 * las escrituras: el `then` de un guardado que recarga no le quita el indicador a esa recarga.
 *
 * @param vm el componente; su `$f7` se resuelve al encender, no al construir
 */
export function esperaDelCarril(vm) {
    let testigo = null;
    return function(si) {
        const anterior = testigo;
        testigo = si ? pedirEspera(vm.$f7) : null;
        soltarEspera(anterior);
    };
}
